import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeHighlight from 'rehype-highlight';
import { visit } from 'unist-util-visit';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeFormat from 'rehype-format';
import rehypeRaw from 'rehype-raw';
import { motion, AnimatePresence } from 'framer-motion';
import 'katex/dist/katex.min.css';
import './code.css';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Modal,
  Stack, Box,
  Avatar,
  ModalDialog,
  DialogTitle,
  DialogContent,
  ModalClose,
  CircularProgress,
  Tooltip,
  IconButton,
  Typography,
  Card,
  Button,
  Link,
  CssVarsProvider,
  Skeleton,
  AspectRatio,
  Chip,
  Menu,
  MenuItem,
  ListItemDecorator
} from '@mui/joy';
import { Transition } from 'react-transition-group';
import { Check, Security, CopyAll, CreditCard, FilterHdr, Fullscreen, GitHub, LinkOffSharp, PestControl, MoreVert, Download, Description, PictureAsPdf, TextFields, AllInclusive } from '@mui/icons-material';
import { RiGithubFill } from 'react-icons/ri';
import { BsRobot } from "react-icons/bs";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';
import docx from 'remark-docx';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../LanguageProvider';
import { useNavigate } from 'react-router-dom';

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    console.log('Text copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy text: ', err);
  });
};

const addButtonInsideHljs = () => {
  return (tree) => {
    visit(tree, 'element', (node) => {
      if (node.properties && node.properties.className && node.properties.className.includes('hljs')) {
        const buttonNode = {
          type: 'element',
          tagName: 'button',
          properties: { className: ['hljs-button'], onClick: () => handleCopy(node) },
          children: [{ type: 'text', value: 'Copy' }]
        };
        node.children.push(buttonNode);
      }
    });
  };
};

const handleCopy = (node) => {
  if (!node) {
    console.error('Node is undefined or null');
    return;
  }

  const codeText = extractTextFromSpans(node);

  copyToClipboard(codeText);
};

const extractTextFromSpans = (node) => {
  let result = '';

  const traverse = (currentNode) => {
    if (!currentNode) return;

    if (currentNode.type === 'element') {
      if (currentNode.tagName === 'button') {
        return;
      }

      currentNode.children?.forEach(child => {
        if (child.type === 'text') {
          result += child.value;
        } else if (child.type === 'element') {
          traverse(child);
        }
      });
    } else if (currentNode.type === 'text') {
      result += currentNode.value;
    }
  };

  traverse(node);
  return result;
};

// Document conversion functions
const convertMarkdownToHtml = async (markdown) => {
  const result = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkBreaks)
    .use(remarkMath)
    .use(remarkRehype)
    .use(rehypeKatex)
    .use(rehypeHighlight)
    .use(rehypeStringify)
    .process(markdown);

  return String(result);
};

const convertMarkdownToText = (markdown) => {
  // Remove markdown formatting
  return markdown
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/`([^`]+)`/g, '$1') // Remove inline code
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.*?)\*/g, '$1') // Remove italic
    .replace(/~~(.*?)~~/g, '$1') // Remove strikethrough
    .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
    .replace(/^#+\s+/gm, '') // Remove headers
    .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
    .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered list markers
    .replace(/^\s*>\s+/gm, '') // Remove blockquotes
    .replace(/\n{3,}/g, '\n\n') // Normalize line breaks
    .trim();
};

const downloadAsPDF = async (content, filename = 'document.pdf') => {
  try {
    const html = await convertMarkdownToHtml(content);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.style.padding = '20px';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    tempDiv.style.fontSize = '12px';
    tempDiv.style.lineHeight = '1.5';
    tempDiv.style.color = '#000';
    tempDiv.style.backgroundColor = '#fff';
    tempDiv.style.width = '800px';
    document.body.appendChild(tempDiv);

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });

    document.body.removeChild(tempDiv);

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

function removeAllowedCustomTags(content) {
  const tags = allowedTags.join('|');
  let result = content.replace(new RegExp(`<(${tags})(\\s+[^>]*)?\/>`, 'g'), '');
  result = result.replace(new RegExp(`<(${tags})(\\s+[^>]*)?>`, 'g'), '');
  result = result.replace(new RegExp(`</(${tags})>`, 'g'), '');
  return result;
}

const downloadAsDOCX = async (content, filename = 'document.docx') => {
  try {
    const cleanContent = removeAllowedCustomTags(content);
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkBreaks)
      .use(remarkMath)
      .use(docx, {
        output: 'blob',
        styles: {
          default: {
            document: {
              paragraph: {
                spacing: {
                  before: 240,
                  after: 120,
                },
                run: {
                  size: 24
                }
              },
              table: {
                cell: {
                  margin: { top: 200, bottom: 200, left: 200, right: 200 }
                }
              }
            },
            listParagraph: {
              paragraph: {
                spacing: {
                  before: 0,
                  after: 0,
                },
                run: {
                  size: 24
                }
              }
            }
          }
        }
      });

    const file = await processor.process(cleanContent);
    const blob = await file.result;

    saveAs(blob, filename);
  } catch (error) {
    console.error('Error generating DOCX:', error);
  }
};

const downloadAsTXT = (content, filename = 'document.txt') => {
  try {
    const text = convertMarkdownToText(content);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, filename);
  } catch (error) {
    console.error('Error generating TXT:', error);
  }
};

function extractCodeAndLang(markdown) {
  const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
  const matches = [];
  let match;

  while ((match = codeBlockRegex.exec(markdown)) !== null) {
    const lang = match[1] || 'plain';
    const code = match[2].trim();
    matches.push({ lang, code });
  }

  return matches;
}

const CustomLink = ({ href, children }) => {
  return (
    <a href={href} style={{ color: 'white', textDecoration: 'none' }}>
      {children}
    </a>
  );
};

const CustomMessage = ({ model, children }) => {
  let content = '';
  if (Array.isArray(children)) {
    content = children.map(child =>
      typeof child === 'string' ? child : (child?.props?.children ? child.props.children : '')
    ).join('');
  } else if (typeof children === 'string') {
    content = children;
  } else if (children?.props?.children) {
    content = children.props.children;
  }
  return (
    <Card variant='soft' sx={{ mt: 1, borderRadius: 20, py: 0 }}>
      <Card variant='plain' sx={{ borderRadius: "20px 20px 0 0", mt: 0, mx: -2, py: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", py: 0 }}>
          <Avatar src={getApiUrl("/v3/ai/model/avatar/" + model)} />
          <Typography level="title-md">{model}</Typography>
        </Stack>
      </Card>
      <div style={{ marginTop: -10, fontSize: "0.8rem" }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
          rehypePlugins={[rehypeCustomRawFilter, rehypeRaw, rehypeHighlight, rehypeKatex, rehypeFormat]}
        >
          {content}
        </ReactMarkdown>
      </div>
    </Card>
  );
};

const CustomImg = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openImage, setOpenImage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageAvailable, setImageAvailable] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const { currentLanguageData } = useLanguage();
  const { user, checkRateLimit } = useAuth();
  const navigate = useNavigate();

  // Max number of retries for loading image
  const MAX_RETRIES = 3;
  // Delay between retries in ms
  const RETRY_DELAY = 1000;

  const getImageDimensions = (url) => {
    try {
      const urlObj = new URL(url);
      const width = parseInt(urlObj.searchParams.get('width')) || 1024;
      const height = parseInt(urlObj.searchParams.get('height')) || 576;
      const aspectRatio = width / height;
      return { width, height, aspectRatio };
    } catch (error) {
      return { width: 1024, height: 576, aspectRatio: 1024 / 576 };
    }
  };

  const getImageUrlWithParams = (url) => {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('token', user?.jwt_token);
      return urlObj.toString();
    } catch (error) {
      return url;
    }
  };

  const { width, height, aspectRatio } = getImageDimensions(props.src);

  const checkImageAvailability = (url, onSuccess, onError) => {
    const img = new window.Image();
    img.src = url;
    img.onload = onSuccess;
    img.onerror = onError;
  };

  // Retry logic with capped attempts and delay
  const tryLoadImage = React.useCallback(() => {
    const imageUrl = getImageUrlWithParams(props.src);
    checkImageAvailability(
      imageUrl,
      () => {
        setImageAvailable(true);
        setLoadingTimeout(false);
      },
      () => {
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
          }, RETRY_DELAY);
        } else {
          setImageAvailable(false);
        }
      }
    );
  }, [props.src, retryCount]);

  useEffect(() => {
    setImageAvailable(false);
    setRetryCount(0);
    setLoadingTimeout(false);
    setImageLoaded(false);
  }, [props.src]);

  useEffect(() => {
    if (!imageAvailable && retryCount <= MAX_RETRIES) {
      tryLoadImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount, imageAvailable, tryLoadImage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTimeout(true);
    }, 25000);
    return () => clearTimeout(timer);
  }, [props.src]);

  const handleImageClick = () => {
    setOpenImage(true);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if(!checkRateLimit.canImageGenerate){
    return (
      <Card variant='soft' sx={{borderRadius: '12px', mt: 1}}>
        <Typography level='body-sm'>{currentLanguageData.imageFailed}</Typography>
        <Button onClick={() => navigate('/plans')} startDecorator={<AllInclusive/>} variant="solid" color='warning' sx={{borderRadius: 20, my: 1}}>{currentLanguageData.newSubscription}</Button>
      </Card>
    )
  }

  return (
    <>
      <div
        onClick={handleImageClick}
        style={{
          cursor: 'pointer',
          borderRadius: '12px',
          maxWidth: '100%',
          maxHeight: '220px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          marginTop: '4px',
        }}
      >
        <div
          style={{
            width: '100%',
            aspectRatio: `${width} / ${height}`,
            maxHeight: '220px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AnimatePresence mode="wait">
            {imageAvailable ? (
              <motion.div
                key="loaded"
                initial={{
                  opacity: 0,
                  clipPath: 'circle(0% at 50% 50%)',
                  filter: 'blur(20px)',
                }}
                animate={{
                  opacity: 1,
                  clipPath: 'circle(150% at 50% 50%)',
                  filter: 'blur(0px)',
                }}
                exit={{
                  opacity: 0,
                  clipPath: 'circle(0% at 50% 50%)',
                  filter: 'blur(20px)',
                }}
                transition={{
                  duration: 0.6,
                  ease: [0.4, 0, 0.2, 1],
                }}
                style={{ borderRadius: '12px', width: '100%', height: '100%', justifyContent: 'flex-start', position: 'absolute', top: 0, left: 0 }}
              >
                <img
                  {...props}
                  style={{ borderRadius: '12px', width: '100%', height: '100%', objectFit: 'cover', maxWidth: '100%', maxHeight: '220px', position: 'absolute', top: 0, left: 0 }}
                  src={getImageUrlWithParams(props.src)}
                  onLoad={handleImageLoad}
                  onError={() => setImageAvailable(false)}
                />
              </motion.div>
            ) : (
              <motion.div
                key="loading"
                initial={{
                  opacity: 1,
                  clipPath: 'circle(150% at 50% 50%)',
                  filter: 'blur(0px)',
                }}
                animate={{
                  opacity: 1,
                  clipPath: 'circle(150% at 50% 50%)',
                  filter: 'blur(0px)',
                }}
                exit={{
                  opacity: 0,
                  clipPath: 'circle(0% at 50% 50%)',
                  filter: 'blur(20px)',
                }}
                transition={{
                  duration: 0.6,
                  ease: [0.4, 0, 0.2, 1],
                }}
                style={{ borderRadius: '12px', width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
              >
                <Stack sx={{ width: '100%', height: '100%' }}>
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}>
                    <Skeleton sx={{ zIndex: 0, width: '100%', height: '100%' }} loading={true}>
                      <img alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', maxHeight: '220px', position: 'absolute', top: 0, left: 0 }} />
                    </Skeleton>
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 1,
                    }}>
                      <CircularProgress
                        variant="plain"
                        size="md"
                        thickness={4}
                        sx={{ mt: 1, mb: 1 }}
                        color={loadingTimeout ? 'danger' : 'neutral'}
                      >
                        <FilterHdr color={loadingTimeout ? 'danger' : 'neutral'} />
                      </CircularProgress>
                    </Box>
                  </Box>
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Transition in={openImage} timeout={400}>
        {(state) => (
          <Modal
            keepMounted
            open={!['exited', 'exiting'].includes(state)}
            onClose={() => setOpenImage(false)}
            slotProps={{
              backdrop: {
                sx: {
                  opacity: 0,
                  backdropFilter: 'none',
                  transition: `opacity 400ms, backdrop-filter 400ms`,
                  ...(state === 'entering' || state === 'entered' ? { opacity: 1, backdropFilter: 'blur(8px)' } : {}),
                },
              },
            }}
            sx={{
              zIndex: 10000,
              visibility: state === 'exited' ? 'hidden' : 'visible',
            }}
          >
            <AnimatePresence>
              {(['entering', 'entered'].includes(state)) && (
                <motion.div
                  key="modal-dialog"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
                >
                  <ModalDialog
                    layout='center'
                    variant="plain"
                    sx={{
                      opacity: 1,
                      p: 0,
                      borderRadius: 20,
                      backgroundColor: 'transparent',
                      transition: `opacity 300ms`,
                    }}
                  >
                    <ModalClose variant='plain' sx={{ borderRadius: 250, zIndex: 999999 }} />
                    <DialogContent>
                      {imageAvailable ? (
                        <motion.img
                          initial={{ opacity: 0, scale: 0.96 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.96 }}
                          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                          style={{ borderRadius: 20, maxWidth: '100%' }}
                          src={getImageUrlWithParams(props.src)}
                          onLoad={handleImageLoad}
                          onError={() => setImageAvailable(false)}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <CircularProgress
                            variant="soft"
                            sx={{ mt: 1, mb: 1 }}
                            color={loadingTimeout ? 'danger' : 'neutral'}
                          />
                        </Box>
                      )}
                    </DialogContent>
                  </ModalDialog>
                </motion.div>
              )}
            </AnimatePresence>
          </Modal>
        )}
      </Transition>
    </>
  );
};

export const CustomImg2 = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openImage, setOpenImage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageAvailable, setImageAvailable] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const checkImageAvailability = (url, onSuccess, onError) => {
    const img = new Image();
    img.src = url;
    img.onload = onSuccess;
    img.onerror = onError;
  };

  const retryLoadingImage = () => {
    setRetryCount(retryCount + 1);
    checkImageAvailability(
      props.src,
      () => {
        setImageAvailable(true);
        setLoadingTimeout(false);
      },
      () => {
        console.error(`Image failed to load, retrying... (${retryCount + 1})`);
        retryLoadingImage();
      }
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTimeout(true);
    }, 25000);

    retryLoadingImage();

    return () => clearTimeout(timer);
  }, [props.src]);

  const handleImageClick = () => {
    setOpenImage(true);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    console.error('Image failed to load');
    setImageAvailable(false);
  };

  return (
    <>
      <div
        onClick={handleImageClick}
        style={{
          width: '100%', height: '100%'
        }}
      >
        <img
          {...props}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          src={props.src}
          onLoad={handleImageLoad}
          onError={retryLoadingImage}
        />
      </div>

      <Transition in={openImage} timeout={400}>
        {(state) => (
          <Modal
            keepMounted
            open={!['exited', 'exiting'].includes(state)}
            onClose={() => setOpenImage(false)}
            slotProps={{
              backdrop: {
                sx: {
                  opacity: 0,
                  backdropFilter: 'none',
                  transition: `opacity 400ms, backdrop-filter 400ms`,
                  ...(state === 'entering' || state === 'entered' ? { opacity: 1, backdropFilter: 'blur(8px)' } : {}),
                },
              },
            }}
            sx={{
              zIndex: 10000,
              visibility: state === 'exited' ? 'hidden' : 'visible',
            }}
          >
            <ModalDialog
              layout={isMobile ? 'fullscreen' : 'center'}
              variant='plain'
              sx={{
                opacity: 0,
                borderRadius: 20,
                transition: `opacity 300ms`,
                ...(state === 'entering' || state === 'entered' ? { opacity: 1 } : {}),
              }}
            >
              <ModalClose sx={{ borderRadius: 250 }} />
              <DialogTitle>Image</DialogTitle>
              <DialogContent>
                {imageAvailable ? (
                  <img
                    style={{ borderRadius: '12px', maxWidth: '100%' }}
                    src={props.src}
                    onLoad={handleImageLoad}
                    onError={retryLoadingImage}
                  />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress
                      variant='soft'
                      sx={{ mt: 1, mb: 1 }}
                      color={loadingTimeout ? 'danger' : 'neutral'}
                    />
                  </Box>
                )}
              </DialogContent>
            </ModalDialog>
          </Modal>
        )}
      </Transition>
    </>
  );
};

const CustomCodeBlock = ({ setSilaneCode, silaneEnabled, silaneOpen, silaneOpened, isMobile, inline, className, children, ...props }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const match = /language-([\w#]+)/.exec(className || "");
  const language = match ? match[1] : "text";

  const codeRef = useRef(null);

  const handleCopy = () => {
    if (codeRef.current) {
      const textToCopy = codeRef.current.innerText;
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
    }
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [copied]);

  const supportedLanguages = ["C#", "csharp", "java", "javascript", "typescript", "python", "c", "cpp", "go", "golang"];

  return !inline && match ? (
    <div className="code-block-container" style={{ marginTop: 20 }}>
      <div style={{ position: 'absolute', top: 8, bottom: 50, left: 0, right: 8, pointerEvents: 'none' }}>
        <Tooltip
          sx={{
            position: 'sticky',
            pointerEvents: 'auto',
            top: 0,
            right: 0,
            zIndex: 2,
            float: 'right'
          }}
          title={copied ? "Copied!" : "Click to copy"}
          variant="outlined"
          arrow
          placement="bottom"
        >
          <IconButton style={{ backgroundColor: theme.palette.codeBtn, borderRadius: 250 }} variant='plain' size="sm" onClick={handleCopy}>
            {copied ? <Check /> : <CopyAll />}
          </IconButton>
        </Tooltip>
      </div>
      <div className="code-block-header">
        <Typography sx={{ fontWeight: 600, mt: -0.2 }} level="body-sm">
          {language.toUpperCase()}
        </Typography>
      </div>

      <pre className={`hljs ${className}`}>
        <code ref={codeRef} {...props} className={className}>
          {children}
        </code>
      </pre>

      {(!isMobile && supportedLanguages.includes(language)) && (
        <Button disabled={silaneEnabled} onClick={() => {
          if (!silaneOpened) {
            setSilaneCode({
              code: codeRef.current.innerText,
              lang: language
            });
          }
          silaneOpen(!silaneOpened);
        }}
          startDecorator={<BsRobot />} variant="soft" color="primary" sx={{ position: 'absolute', right: 10, bottom: 10, borderRadius: 250 }} size="sm">
          Silane
        </Button>
      )}
    </div>
  ) : (
    <span style={{ borderRadius: 4, backgroundColor: theme.palette.highlight, padding: "0px 2px 0px 2px", color: theme.palette.highlightText, whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {children}
    </span>
  );
};

const allowedTags = ["gitcommit", "payment", "map", "reply", "state", "message"];
const rehypeCustomRawFilter = () => {
  return (tree) => {
    visit(tree, "raw", (node, index, parent) => {
      const tagName = node.value.match(/^<(\w+)/)?.[1];
      const content = node.value.match(/content="([^"]+)"/)?.[1];

      if (tagName === "state" && content) {
        const parts = node.value.split(/<state[^>]*>/);
        const newNodes = [];

        newNodes.push({
          type: "element",
          tagName: "state",
          properties: { content },
          children: []
        });

        if (parts[1]) {
          const afterText = parts[1].replace(/<\/state>/, '').trim();
          if (afterText) {
            newNodes.push({
              type: "text",
              value: afterText
            });
          }
        }

        parent.children.splice(index, 1, ...newNodes);
      } else if (!allowedTags.includes(tagName)) {
        parent.children[index] = {
          type: "text",
          value: node.value,
        };
      }
    });
  };
};

const formatDateManual = (dateString) => {
  const date = new Date(dateString);
  const options = { month: 'short' };
  const month = date.toLocaleString('en-US', options);
  const day = date.getDate();
  return `${month} ${day < 10 ? '0' + day : day}`;
};

const CustomPaymentTag = ({ sum, currency, message, service }) => {

  const [load, setLoad] = useState(false);

  return <Card
    variant='soft'
    sx={{
      mt: 2.4, mb: 2,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 220,
      justifyContent: 'center',
      py: 2,
      overflow: 'hidden'
    }}>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box>
        <Chip
          size="md"
          sx={{ aspectRatio: '1/1', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
          color="primary"
          variant="soft"
        >
          <CreditCard sx={{ width: '18px', height: '18px', marginTop: "4.45px" }} />
        </Chip>
        <Typography level='body-xs' sx={{ mb: 0.1, ml: 1 }}>· · · · 0512</Typography>
      </Box>
      <Chip
        size="md"
        sx={{ alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', px: 1.8, height: '34px' }}
        color="primary"
        variant="soft"
      >
        {service}
      </Chip>
    </Box>
    <Typography>{message}</Typography>
    <Button loading={load} onClick={() => setLoad(true)} variant="soft" color="primary" sx={{ borderRadius: 30 }}>Pay {sum} {currency}</Button>
  </Card>;
};

const CustomMapTag = ({ url }) => {
  const [openMap, setOpenMap] = useState(false);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      setTimeout(() => setLoading(false), 1000);
    };

    iframe.addEventListener('load', handleLoad);

    return () => iframe.removeEventListener('load', handleLoad);
  }, []);

  const [position, setPosition] = useState({ latitude: null, longitude: null });

  useEffect(() => {
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
        console.log("Geolocation: " + position.latitude + " " + position.longitude)
      },
      (err) => {
        setPosition(null);
        console.log("Geolocation: " + err.message)
      }
    );
  }, []);

  return <>
    <Card
      variant='soft'
      sx={{
        mt: 2.4, mb: 2,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 220,
        height: 220,
        justifyContent: 'center',
        p: 0,
        overflow: 'hidden'
      }}>
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CircularProgress
            variant='soft'
            sx={{ mt: 1, mb: 1 }}
          />
        </Box>
      )}
      <IconButton onClick={() => setOpenMap(true)} variant='soft' sx={{ position: 'absolute', top: 8, right: 8, }}><Fullscreen /></IconButton>
      <iframe
        ref={iframeRef}
        src={formatMapUrl(url) + "&isRoute=true" + (position ? `&position=${position.latitude},${position.longitude}` : '')}
        style={{ border: 'none', width: '100%', height: '100%', visibility: loading ? "hidden" : "visible" }}
      />
    </Card >

    <Transition in={openMap} timeout={400}>
      {(state) => (
        <Modal
          keepMounted
          open={!['exited', 'exiting'].includes(state)}
          onClose={() => setOpenMap(false)}
          slotProps={{
            backdrop: {
              sx: {
                opacity: 0,
                backdropFilter: 'none',
                transition: `opacity 400ms, backdrop-filter 400ms`,
                ...(state === 'entering' || state === 'entered' ? { opacity: 1, backdropFilter: 'blur(8px)' } : {}),
              },
            },
          }}
          sx={{
            zIndex: 10000,
            visibility: state === 'exited' ? 'hidden' : 'visible',
          }}
        >
          <ModalDialog
            layout='fullscreen'
            sx={{
              opacity: 0,
              p: 0,
              pt: 1.5,
              transition: `opacity 300ms`,
              ...(state === 'entering' || state === 'entered' ? { opacity: 1 } : {}),
            }}
          >
            <ModalClose />
            <DialogTitle sx={{ ml: 2, mb: 1 }}>Map</DialogTitle>
            <DialogContent>
              <iframe
                ref={iframeRef}
                src={formatMapUrl(url) + "&isRoute=true" + (position ? `&position=${position.latitude},${position.longitude}` : '')}
                style={{ display: openMap ? "block" : "none", border: 'none', width: '100%', height: '100%', minWidth: '400px', minHeight: '400px', visibility: loading ? "hidden" : "visible" }}
              />
            </DialogContent>
          </ModalDialog>
        </Modal>
      )}
    </Transition>
  </>;
};

const formatMapUrl = (url) => {
  if (!/^https?:\/\//i.test(url)) {
    url = 'https://' + url;
  }

  return url.replace(/\s/g, '%20');
};

const CustomGitCommitTag = ({ repo, date, msg, url }) => {
  const handleClick = (link) => {
    window.open(link, '_blank');
  };

  const getRepositoryUrl = (url) => {
    return url.split('/commit')[0];
  };

  return <Card
    variant='soft'
    sx={{
      mt: 2, mb: 2,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 220,
      justifyContent: 'center',
      py: 2,
      overflow: 'hidden'
    }}>
    <RiGithubFill style={{ position: 'absolute', fontSize: 220, opacity: 0.1, transform: "rotate(-15deg)", left: -80, pointerEvents: 'none' }} />
    <Typography
      textColor="grey"
      startDecorator={
        <Link
          color="neutral"
          underline='always'
          target="_blank"
          href={getRepositoryUrl(url)}
          sx={{ fontSize: 'sm' }}
        >
          {repo}
        </Link>
      }
      sx={{ fontSize: 'sm' }}
    >
      on {formatDateManual(date)}
    </Typography>
    <Typography sx={{ fontWeight: 'lg', fontSize: 'sm', mt: -1 }}>
      {msg}
    </Typography>
    <Button onClick={() => handleClick(url)}>Visit commit</Button>
  </Card>;
};

const CustomReplyTag = ({ children }) => {

  return (
    <Card
      variant="outlined"
      sx={{
        mt: 2.5,
        mb: -1,
        py: 1,
        borderLeft: "4px solid rgb(146, 121, 203)",
        backgroundColor: "rgba(163, 121, 203, 0.1)",
        borderRadius: 12,
        textAlign: 'left',
        position: 'relative'
      }}
    >
      <Typography sx={{ mx: -0.5 }} noWrap level='body-xs'>{children.props.children}</Typography>
    </Card>
  );
};

const CustomStateTag = ({ content }) => {
  const text = content?.replace(/"/g, '') || '';
  return (
    <Box sx={{ mt: 2.5 }}>
      <Chip
        size="md"
        variant="soft"
        color="primary"
        startDecorator={text == "Normal" ? <Security /> : <PestControl />}
        sx={{
          mb: 0.5,
          height: 'auto',
          borderRadius: 250,
          display: 'inline-flex',
          verticalAlign: 'middle',
          fontSize: '12px',
          backgroundColor: 'rgba(163, 121, 203, 0.1)',
          color: '#A379CB',
          borderColor: 'rgba(163, 121, 203, 0.4)',
        }}
      >
        {text}
      </Chip>
      <br />
    </Box>
  );
};

const ContextMenu = ({ content, anchorEl, open, onClose, currentLanguageData }) => {
  const handleCopyMessage = () => {
    copyToClipboard(content);
    onClose();
  };

  const handleDownloadPDF = () => {
    downloadAsPDF(content, 'message.pdf');
    onClose();
  };

  const handleDownloadDOCX = () => {
    downloadAsDOCX(content, 'message.docx');
    onClose();
  };

  const handleDownloadTXT = () => {
    downloadAsTXT(content, 'message.txt');
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      placement="bottom-start"
      variant='plain'
      sx={{
        borderRadius: 20,
        zIndex: 10001,
        '& .MuiMenuItem-root': {
          minWidth: 180,
        },
      }}
    >
      <MenuItem onClick={handleCopyMessage}>
        <ListItemDecorator>
          <CopyAll fontSize="small" />
        </ListItemDecorator>
        {currentLanguageData?.copyMessage || "Copy message"}
      </MenuItem>
      <MenuItem onClick={handleDownloadDOCX}>
        <ListItemDecorator>
          <Description fontSize="small" />
        </ListItemDecorator>
        {currentLanguageData?.downloadDOCX || "Download DOCX"}
      </MenuItem>
      <MenuItem onClick={handleDownloadPDF}>
        <ListItemDecorator>
          <PictureAsPdf fontSize="small" />
        </ListItemDecorator>
        {currentLanguageData?.downloadPDF || "Download PDF"}
      </MenuItem>
      <MenuItem onClick={handleDownloadTXT}>
        <ListItemDecorator>
          <TextFields fontSize="small" />
        </ListItemDecorator>
        {currentLanguageData?.downloadTXT || "Download TXT"}
      </MenuItem>
    </Menu>
  );
};

const MarkdownRenderer = ({ silaneOpen, setSilaneCode, silaneOpened, silaneEnabled, isMobile, content, setOpen, url, currentLanguageData, isAi = false }) => {
  const regex = /<reply>(.*?)<\/reply>(.*)/s;

  const matchReply = content.match(regex);

  const replyMessage = matchReply ? matchReply[1] || "" : "";
  const afterReply = matchReply ? matchReply[2] || "" : content;

  const [contextMenu, setContextMenu] = useState({
    mouseX: null,
    mouseY: null,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.mouseY !== null) {
        setContextMenu({
          mouseX: null,
          mouseY: null,
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [contextMenu.mouseY]);

  const handleContextMenu = (event) => {
    if (contextMenu.mouseY !== null) {
      setContextMenu({
        mouseX: null,
        mouseY: null,
      });
      return;
    }

    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleClose = () => {
    setContextMenu({
      mouseX: null,
      mouseY: null,
    });
  };

  const messageRegex = /([\s\S]*?)(<message[^>]*>[\s\S]*?<\/message>)([\s\S]*)/;
  const match = content.match(messageRegex);

  let before = '', messageBlock = '', after = '', hasMessage = false;
  if (match) {
    before = match[1].trim();
    messageBlock = match[2];
    after = match[3].trim();
    hasMessage = true;
  }

  // Extract message content and model if present
  let messageContent = '';
  let messageModel = '';
  if (messageBlock) {
    const innerMatch = messageBlock.match(/<message[^>]*>([\s\S]*?)<\/message>/);
    if (innerMatch) {
      messageContent = innerMatch[1].trim();
      // Extract model attribute
      const modelMatch = messageBlock.match(/model=["']([^"']+)["']/);
      if (modelMatch) {
        messageModel = modelMatch[1];
      }
    }
  }

  // Preprocess content to replace text tags with state tags
  const preprocessContent = (text) => {
    return text
      .replace(/<DeveloperMode\/>/g, '<state content="Developer Mode"/>')
      .replace(/<Normal\/>/g, '<state content="Normal"/>');
  };

  return (
    <>
      <div onContextMenu={handleContextMenu}>
        {replyMessage && (
          <CustomReplyTag>
            <ReactMarkdown
              components={{
                code: (props) => <CustomCodeBlock {...props} silaneEnabled={silaneEnabled} setSilaneCode={setSilaneCode} silaneOpened={silaneOpened} silaneOpen={silaneOpen} isMobile={isMobile} />,
                img: CustomImg,
                a: CustomLink,
                gitcommit: CustomGitCommitTag,
                payment: CustomPaymentTag,
                map: CustomMapTag,
                state: CustomStateTag,
              }}
              remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
              rehypePlugins={[rehypeCustomRawFilter, rehypeRaw, rehypeHighlight, rehypeKatex, rehypeFormat]}
            >
              {preprocessContent(replyMessage)}
            </ReactMarkdown>
          </CustomReplyTag>
        )}

        {afterReply && (
          hasMessage ? (
            <>
              {before && (
                <ReactMarkdown
                  components={{
                    code: (props) => <CustomCodeBlock {...props} silaneEnabled={silaneEnabled} setSilaneCode={setSilaneCode} silaneOpened={silaneOpened} silaneOpen={silaneOpen} isMobile={isMobile} />,
                    img: CustomImg,
                    a: CustomLink,
                    gitcommit: CustomGitCommitTag,
                    payment: CustomPaymentTag,
                    map: CustomMapTag,
                    state: CustomStateTag,
                    message: CustomMessage
                  }}
                  remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
                  rehypePlugins={[rehypeCustomRawFilter, rehypeRaw, rehypeHighlight, rehypeKatex, rehypeFormat]}
                >
                  {preprocessContent(before)}
                </ReactMarkdown>
              )}
              {messageContent && (
                <CustomMessage model={messageModel}>
                  {messageContent}
                </CustomMessage>
              )}
              {after && (
                <ReactMarkdown
                  components={{
                    code: (props) => <CustomCodeBlock {...props} silaneEnabled={silaneEnabled} setSilaneCode={setSilaneCode} silaneOpened={silaneOpened} silaneOpen={silaneOpen} isMobile={isMobile} />,
                    img: CustomImg,
                    a: CustomLink,
                    gitcommit: CustomGitCommitTag,
                    payment: CustomPaymentTag,
                    map: CustomMapTag,
                    state: CustomStateTag,
                    message: CustomMessage
                  }}
                  remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
                  rehypePlugins={[rehypeCustomRawFilter, rehypeRaw, rehypeHighlight, rehypeKatex, rehypeFormat]}
                >
                  {preprocessContent(after)}
                </ReactMarkdown>
              )}
            </>
          ) : (
            <ReactMarkdown
              components={{
                code: (props) => <CustomCodeBlock {...props} silaneEnabled={silaneEnabled} setSilaneCode={setSilaneCode} silaneOpened={silaneOpened} silaneOpen={silaneOpen} isMobile={isMobile} />,
                img: CustomImg,
                a: CustomLink,
                gitcommit: CustomGitCommitTag,
                payment: CustomPaymentTag,
                map: CustomMapTag,
                state: CustomStateTag,
                message: CustomMessage
              }}
              remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
              rehypePlugins={[rehypeCustomRawFilter, rehypeRaw, rehypeHighlight, rehypeKatex, rehypeFormat]}
            >
              {preprocessContent(afterReply)}
            </ReactMarkdown>
          ))}
      </div>

      {isAi == true && (
        <ContextMenu
          content={content}
          anchorEl={contextMenu.mouseY !== null ? { getBoundingClientRect: () => ({ top: contextMenu.mouseY, left: contextMenu.mouseX, width: 0, height: 0 }) } : null}
          open={contextMenu.mouseY !== null}
          onClose={handleClose}
          currentLanguageData={currentLanguageData}
        />
      )}
    </>
  );
};

const MarkdownRendererDef = ({ content }) => {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p style={{ fontSize: '12px', opacity: 0.8 }}>{children}</p>
      }}
      remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
      rehypePlugins={[rehypeCustomRawFilter, rehypeRaw, rehypeHighlight, rehypeKatex, rehypeFormat]}
    >
      {content}
    </ReactMarkdown>
  );
};

export { MarkdownRenderer, MarkdownRendererDef };